// Session-scoped registry of datasource ids the server confirmed dead (404/403).
// Local absence alone never proves deletion — unfetched/evicted/offline look the same.

const deadDatasourceIds = new Set<string>();

export function markDatasourceDead(dataSourceId: string): void {
  deadDatasourceIds.add(String(dataSourceId));
}

export function isDatasourceDead(dataSourceId: string): boolean {
  return deadDatasourceIds.has(String(dataSourceId));
}

export function clearDatasourceDead(dataSourceId: string): void {
  deadDatasourceIds.delete(String(dataSourceId));
}

// Created this session but not yet synced — their 404s mean "not on the server
// yet", never "deleted". Set synchronously at mutation start so no fetch can
// race the IDB commit.
const creatingDatasourceIds = new Set<string>();

export function markDatasourceCreating(dataSourceId: string): void {
  creatingDatasourceIds.add(String(dataSourceId));
}

export function isDatasourceCreating(dataSourceId: string): boolean {
  return creatingDatasourceIds.has(String(dataSourceId));
}

export function clearDatasourceCreating(dataSourceId: string): void {
  creatingDatasourceIds.delete(String(dataSourceId));
}

// The UI renders from React context, not IDB — the cascade's writes are
// invisible to it without this.
export const DATASOURCE_CASCADE_EVENT = "datasource:cascade-deleted";

export interface DatasourceCascadeDetail {
  dataSourceIds: string[];
}

export function emitDatasourceCascade(detail: DatasourceCascadeDetail): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(DATASOURCE_CASCADE_EVENT, { detail }));
}

// ─── Liveness verification ──────────────────────────────────────────
// Relation/rollup targets are never fetched just to render a value, so their
// state is unknown until asked. Deduped to one check per id.

type Listener = () => void;
const listeners = new Set<Listener>();
const verifying = new Set<string>();
const deferredWhileOffline = new Set<string>();

if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    const pending = [...deferredWhileOffline];
    deferredWhileOffline.clear();
    pending.forEach((id) => verifyDatasource(id));
  });
}

export function subscribeDatasourceLiveness(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify(): void {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // one bad subscriber must not break the rest

    }
  });
}

/** Fire-and-forget: resolves whether a datasource still exists server-side. */
export function verifyDatasource(dataSourceId: string): void {
  const id = String(dataSourceId || "");
  if (!id || typeof window === "undefined") return;
  if (deadDatasourceIds.has(id) || creatingDatasourceIds.has(id) || verifying.has(id)) return;
  // Offline the server can't answer — retried on reconnect, never marked dead.
  if (!navigator.onLine) {
    deferredWhileOffline.add(id);
    return;
  }

  verifying.add(id);
  (async () => {
    // Dynamic imports: this module is imported BY the IDB layer, so a static
    // import back into it would be a cycle.
    const { getWithAuth } = await import("@/lib/api-helpers");
    const { hasPendingDatasourceCreation } = await import(
      "@/indexedDB/stores/syncQueueStore"
    );
    // Captured before the request — a creation pending then makes a 404 stale.
    const creationPending = await hasPendingDatasourceCreation(id);
    const res: any = await getWithAuth(`/api/database/get-data-source/${id}`);
    if (!res?.isError || (res.status !== 404 && res.status !== 403)) return;
    if (creationPending || creatingDatasourceIds.has(id)) return;
    if (await hasPendingDatasourceCreation(id)) return;
    deadDatasourceIds.add(id);
    notify();
  })()
    .catch(() => {})
    .finally(() => verifying.delete(id));
}
